import { Component, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, NgClass, AsyncPipe } from '@angular/common';
import { SchoolInvitesService } from 'src/app/services/api/consumer/school-invites.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ToastrService } from 'ngx-toastr';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { DirectivesModule } from 'src/app/directives.module';
import { ArticleComponent } from '../../producer/article/article.component';

@Component({
  selector: 'app-school-invites',
  standalone: true,
  templateUrl: './school-invites.component.html',
  styleUrl: './school-invites.component.scss',
  imports: [
    DirectivesModule,
    CommonModule,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    ArticleComponent
  ]

})
export class SchoolInvitesComponent implements OnInit {
  @ViewChild('actionModals') actionModals: any;
  @ViewChild('acceptRequestModal') acceptRequestModal: any;

  // Data sources for async pipe
  private invitesSubject = new BehaviorSubject<any[]>([]);
  private historySubject = new BehaviorSubject<any[]>([]);
  invitesSchoolsList = this.invitesSubject.asObservable();
  invitesHistorySchoolsList = this.historySubject.asObservable();

  invitesData: any[] = [];
  historyData: any[] = [];
  schoolDetails: any[] = [];
  selectAllCheck = false;
  hideButton = true;
  activeTab: 'invites' | 'history' = 'invites';
  currentPage = 1;
  currentHistoryPage = 1;
  schoolLengthLimit = 0;
  schoolHistoryLengthLimit = 0;
  submitted = false;
  schoolCount = 0;

  // Forms
  declineInviteForm: FormGroup;
  schoolInvitesFilterForm: FormGroup;
  schoolInvitesHistoryFilterForm: FormGroup;
  actionForm: FormGroup;

  // Modal state
  actionType: any;
  schoolName: string = '';
  school: any = null;

  // Character count for decline reason
  ShowLenght = 500;
  ShowLenghtError = 500;

  constructor(
    private schoolInvitesService: SchoolInvitesService,
    private router: Router,
    private ngxService: NgxUiLoaderService,
    private fb: FormBuilder,
    private modalService: BsModalService,
    public bsModalRef: BsModalRef,
    private toastr: ToastrService
  ) {
    this.declineInviteForm = this.fb.group({});
    this.schoolInvitesFilterForm = this.fb.group({
      filter_name: [''],
      filterType: [''],
      page: ['']
    });
    this.schoolInvitesHistoryFilterForm = this.fb.group({
      filter_name: [''],
      filterType: [''],
      page: ['']
    });
    this.actionForm = this.fb.group({
      action: [''],
      request_id: [''],
      decline_reason: ['']
    });
  }

  ngOnInit(): void {
    this.loadInvites();
  }

  onTabChange(tab: 'invites' | 'history') {
    this.activeTab = tab;
    if (tab === 'invites') {
      this.currentPage = 1;
      this.loadInvites();
    } else {
      this.currentHistoryPage = 1;
      this.loadHistory();
    }
  }

  loadInvites() {
    this.ngxService.start();
    const params = {
      ...this.schoolInvitesFilterForm.value,
      filterType: 'invites',
      page: this.currentPage
    };
    this.schoolInvitesService.getSchoolInvitesRequestAPI(params).subscribe({
      next: (res: any) => {
        this.invitesData = res.data || [];
        this.invitesSubject.next(this.invitesData);
        this.schoolLengthLimit = res.data?.total_count || 0;
        this.schoolCount = res.school_count || 0;
        this.selectAllCheck = false;
        this.schoolDetails = [];
        this.hideButton = true;
        this.ngxService.stop();
      },
      error: (err: any) => {
        this.toastr.error(err.message || 'Failed to load invites');
        this.ngxService.stop();
      }
    });
  }

  loadHistory() {
    this.ngxService.start();
    const params = {
      ...this.schoolInvitesHistoryFilterForm.value,
      filterType: 'history',
      page: this.currentHistoryPage
    };
    this.schoolInvitesService.getSchoolInvitesHistoryAPI(params).subscribe({
      next: (res: any) => {
        this.historyData = res.data || [];
        this.historySubject.next(this.historyData);
        this.schoolHistoryLengthLimit = res.data?.total_count || 0;
        this.schoolCount = res.school_count || 0;
        this.ngxService.stop();
      },
      error: (err: any) => {
        this.toastr.error(err.message || 'Failed to load history');
        this.ngxService.stop();
      }
    });
  }

  getFilterInputs() {
    if (this.activeTab === 'invites') {
      this.currentPage = 1;
      this.loadInvites();
    } else {
      this.currentHistoryPage = 1;
      this.loadHistory();
    }
  }

  selectAll(type: any, event: any) {
    const checked = event.target.checked;
    if (type === 'All') {
      this.schoolDetails = [];
      this.invitesData.forEach((x: any) => {
        x.isChecked = checked;
        if (checked) {
          this.schoolDetails.push(x);
        }
      });
      this.hideButton = !checked;
    } else {
      type.isChecked = checked;
      if (checked) {
        this.schoolDetails.push(type);
      } else {
        this.schoolDetails = this.schoolDetails.filter(x => x !== type);
      }
      this.hideButton = this.schoolDetails.length === 0;
    }
    this.selectAllCheck = this.invitesData.length === this.schoolDetails.length && this.invitesData.length > 0;
  }

  accepetRequestModal(action: 'accept' | 'decline') {
    this.actionType = action;
    this.bsModalRef = this.modalService.show(this.acceptRequestModal, { class: 'modal-md', backdrop: 'static' });
  }

  actionModal(school: any, action: 'accept' | 'decline') {
    this.school = school;
    this.schoolName = school.school.title;
    this.actionType = action;
    this.bsModalRef = this.modalService.show(this.actionModals, { class: 'modal-md', backdrop: 'static' });
  }

  getFormControlValues() {
    if (this.actionType == 'accept') {
      this.actionForm.patchValue({
        action: 'accept'
      });
      return this.actionForm.value;
    } else if (this.actionType == 'decline') {
      this.actionForm.patchValue({
        action: 'decline'
      });
      return this.actionForm.value;
    } else {
      return this.declineInviteForm.value;
    }
  }

  actions(school: any, action: 'accept' | 'decline') {
    this.actionType = action;
    this.submitted = true;
    this.ngxService.start();
    if ((action === 'accept' || action === 'decline') && this.schoolDetails.length > 0) {
      this.actionForm.patchValue({
        request_id: this.schoolDetails.map(item => item.id).join(',')
      });
    } else {
      this.actionForm.patchValue({
        request_id: school.id.toString()
      });
    }
    this.actionForm.patchValue({ action });
    this.schoolInvitesService.inviteActionAPI(this.getFormControlValues()).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.bsModalRef.hide();
          this.toastr.success(res.message);
          this.schoolDetails = [];
          this.hideButton = true;
          this.loadInvites();
        } else {
          this.toastr.error(res.message);
        }
        this.ngxService.stop();
      },
      error: (err: any) => {
        this.toastr.error(err.statusText + ', Please contact admin');
        this.ngxService.stop();
      }
    });
  }

  checkLength() {
    const timing = this.actionForm.controls['decline_reason'].value || '';
    this.ShowLenght = this.ShowLenghtError - timing.length;
  }

  findNewSchool(): void {
    this.router.navigate(['teacher/find-new-schools']);
  }

  handlePageData(data: any) {
    // Store the page data for later use, e.g. pagination or display
    this.pageData = data;
    // Optionally, update the UI or trigger other logic if needed
    // Example: this.obsArray.next(data.consumer_list || []);
  }
  pageData: any;

}
